// src/utils/requestForToken.js
import { getToken, onMessage, isSupported } from "firebase/messaging";
import { messaging } from "./firebase";
import api from "./api";
import { addNotification } from "./notificationStore";

const VAPID_KEY = "BMads4wiiZsh_08U2ZyNLZqbB4TOrj38PP3UFzpVLh76NGniRSrDF5h-xEkUpz0XtHOOR_GCi0bsT-5aOjbShAw";

// ✅ In-memory only (NO localStorage)
let currentFcmToken = null;

let registerRetryCount = 0;
let registerRetryTimer = null;
const MAX_REGISTER_RETRIES = 10;
const RETRY_BASE_DELAY_MS = 1000;

const ensureMessagingSupported = async () => {
  try {
    const supported = await isSupported();
    if (!supported) {
      console.warn("⚠️ This browser does not support Firebase Cloud Messaging.");
      return null;
    }
    return messaging;
  } catch (err) {
    console.error("❌ Unable to determine messaging support:", err);
    return null;
  }
};

const registerServiceWorker = async () => {
  if (!("serviceWorker" in navigator)) {
    console.warn("⚠️ Service workers are not supported in this browser.");
    return null;
  }

  try {
    const existingRegistration =
      await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
    if (existingRegistration) return existingRegistration;

    return await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  } catch (err) {
    console.error("❌ Failed to register Firebase messaging service worker:", err);
    return null;
  }
};

// ✅ MAIN TOKEN REQUEST (STRICTLY AFTER LOGIN)
export const requestForToken = async () => {
  try {
    // ✅ ✅ ✅ HARD LOGIN GATE (THIS IS THE FIX)
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.warn("⛔ FCM blocked: user not logged in yet.");
      return null;
    }

    // ✅ REUSE TOKEN IN SAME SESSION (NO REGENERATION)
    if (currentFcmToken) {
      console.log("✅ Using existing in-memory FCM token:", currentFcmToken);
      await sendTokenToBackend(currentFcmToken);
      return currentFcmToken;
    }

    const messagingInstance = await ensureMessagingSupported();
    if (!messagingInstance) return null;

    if (typeof Notification === "undefined") {
      console.warn("⚠️ Notification API not available.");
      return null;
    }

    const permission =
      Notification.permission === "default"
        ? await Notification.requestPermission()
        : Notification.permission;

    if (permission !== "granted") {
      console.warn("⚠️ Notification permission not granted.");
      return null;
    }

    const registration = await registerServiceWorker();
    if (!registration) return null;

    if (!VAPID_KEY || VAPID_KEY.length < 80) {
      console.error("❌ Invalid VAPID key.");
      return null;
    }

    const currentToken = await getToken(messagingInstance, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!currentToken) {
      console.warn("⚠️ No registration token available.");
      return null;
    }

    console.log("✅ FCM Token generated:", currentToken);
    currentFcmToken = currentToken;

    await sendTokenToBackend(currentToken);
    return currentToken;
  } catch (err) {
    console.error("❌ Error retrieving token:", err);
    return null;
  }
};

// ✅ LOGOUT (UNREGISTER FROM BACKEND ONLY — NO STORAGE)
export const unregisterToken = async () => {
  const accessToken = localStorage.getItem("accessToken");

  if (!accessToken || !currentFcmToken) {
    console.warn("⚠️ No token to unregister.");
    return;
  }

  try {
    await api.delete("/unregister-device/", {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      data: { fcm_token: currentFcmToken },
    });

    console.log("✅ Token unregistered from backend");

    // ✅ Keep token in memory unless you WANT new one
    // currentFcmToken = null;

  } catch (err) {
    console.error("❌ Failed to unregister token:", err.response?.data || err.message);
  }
};

export const sendTokenToBackend = async (fcmToken) => {
  const tryRegister = async () => {
    const accessToken = localStorage.getItem("accessToken");
    const tokenToRegister = fcmToken;

    if (!tokenToRegister) return;

    if (!accessToken) {
      registerRetryCount++;

      if (registerRetryCount <= MAX_REGISTER_RETRIES) {
        const delay = RETRY_BASE_DELAY_MS * Math.pow(2, registerRetryCount - 1);

        if (registerRetryTimer) clearTimeout(registerRetryTimer);

        registerRetryTimer = setTimeout(
          tryRegister,
          Math.min(delay, 15000)
        );

        console.warn(
          `⏳ Access token not found. Retrying device registration (attempt ${registerRetryCount}/${MAX_REGISTER_RETRIES})...`
        );
      } else {
        console.error(
          "❌ Gave up registering device — access token not available."
        );
      }
      return;
    }

    try {
      await api.post(
        "/register-device/",
        { fcm_token: tokenToRegister },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("✅ Token registered on backend:", tokenToRegister);

      registerRetryCount = 0;
      if (registerRetryTimer) clearTimeout(registerRetryTimer);
    } catch (err) {
      console.error(
        "❌ Backend registration failed:",
        err.response?.data || err.message
      );
    }
  };

  return tryRegister();
};

// ✅ FOREGROUND LISTENER
export const listenToNotifications = () => {
  ensureMessagingSupported().then((messagingInstance) => {
    if (!messagingInstance) return;

    onMessage(messagingInstance, (payload) => {
      console.log("🔔 Notification received in foreground:", payload);

      const title =
        payload.notification?.title || payload.data?.title || "New Notification";
      const body =
        payload.notification?.body || payload.data?.body || "You have a new message";
      const icon =
        payload.notification?.icon || payload.data?.icon || "/firebase-logo.png";

      if (Notification.permission === "granted") {
        new Notification(title, { body, icon });
      }

      addNotification({
        title,
        desc: body,
        time: new Date().toLocaleTimeString(),
      });
    });
  });
};

// ✅ BACKGROUND LISTENER
if (typeof navigator !== "undefined" && navigator.serviceWorker) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    const data = event?.data;

    if (data?.type === "FIREBASE_BG_MESSAGE" && data?.payload) {
      const { title, body, time } = data.payload;

      addNotification({
        title: title || "New Notification",
        desc: body || "You have a new message",
        time: time || new Date().toLocaleTimeString(),
      });
    }
  });
}

// ✅ LOCAL TEST UTILS
export const addLocalNotification = (
  title = "Test notification",
  body = "This is a local test",
  time
) => {
  addNotification({
    title,
    desc: body,
    time: time || new Date().toLocaleTimeString(),
  });
};

export const startLocalNotificationDemo = (intervalMs = 15000) => {
  const id = setInterval(() => {
    addLocalNotification("Demo update", "This arrived locally without API/FCM");
  }, intervalMs);

  return () => clearInterval(id);
};
